import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { 
    getDossier, 
    updateDossier, 
    deleteDocument,
    deleteDossier,
    updateRelance,
    deleteRelance,
    getStatutsConfig,
    getAssignableUsers
} from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { BudgetTab, ChargesTab, FacturationTab, ChantierTab, RefuseReasonDialog, RdvGoogleButton, CoverPhotoBlock } from '../components/DossierExtras';
import { DocumentPreview, UserBadge, colorFromId } from '../components/DocumentPreview';
import { STATUT_LABELS, DOCUMENT_TYPES, DateField } from '../components/dossier/DossierHelpers';
import { NoteDialog } from '../components/dossier/NoteDialog';
import { NotesTab } from '../components/dossier/NotesTab';
import { DossierInfoTab } from '../components/dossier/DossierInfoTab';
import { RelanceDialog } from '../components/dossier/RelanceDialog';
import { UploadDocumentDialog } from '../components/dossier/UploadDocumentDialog';
import { PostDevisChecklist } from '../components/dossier/PostDevisChecklist';
import { PVReceptionTab } from '../components/dossier/PVReceptionTab';
import { getSettings, API_URL } from '../lib/api';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
} from '../components/ui/dialog';
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { 
    ArrowLeft, 
    Phone, 
    Mail, 
    MapPin, 
    Pencil,
    Save,
    Calendar as CalendarIcon,
    Clock,
    FileText,
    MessageSquare,
    Upload,
    Trash2,
    Plus,
    Download,
    Image,
    File,
    Bell,
    CheckCircle2,
    XCircle,
    Euro,
    User,
    History,
    Eye,
    Scale,
    AlertTriangle
} from 'lucide-react';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { toast } from 'sonner';

export const DossierPage = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const fileInputRef = useRef(null);
    const { user } = useAuth();
    const isAdmin = user?.role === 'admin';
    
    const [dossier, setDossier] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [editMode, setEditMode] = useState(false);
    const [statuts, setStatuts] = useState([]);
    const [users, setUsers] = useState([]);
    
    const [showNoteModal, setShowNoteModal] = useState(false);

    const [showRelanceModal, setShowRelanceModal] = useState(false);

    const [showUploadModal, setShowUploadModal] = useState(false);

    const [refuseOpen, setRefuseOpen] = useState(false);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const [refusalReasons, setRefusalReasons] = useState([
        'Trop cher', 'Signé chez un autre fournisseur', 'Projet annulé', 'Budget insuffisant', 'Délais non compatibles', 'Autre'
    ]);
    const [previewDoc, setPreviewDoc] = useState(null);

    useEffect(() => {
        fetchData();
    }, [id]);

    const fetchData = async () => {
        // Primary: display the dossier as soon as we have its data (no blocking
        // on statuts/users/settings which are needed only for the status dropdown
        // and admin editing). Previously the Promise.all made a slow auxiliary
        // request delay the whole page — could leave the user on a 15-30 s spinner.
        try {
            const dossierRes = await getDossier(id);
            setDossier(dossierRes.data);
            setLoading(false);
        } catch (error) {
            toast.error('Erreur lors du chargement');
            navigate('/projets');
            setLoading(false);
            return;
        }

        // Secondary: fetch auxiliary data in the background. Non-blocking.
        try {
            const [statutsRes, usersRes, settingsRes] = await Promise.all([
                getStatutsConfig(),
                getAssignableUsers().catch(() => ({ data: [] })),
                getSettings().catch(() => ({ data: {} })),
            ]);
            setStatuts(statutsRes.data?.statuts || statutsRes.data?.pipeline || []);
            setUsers(usersRes.data);
            if (Array.isArray(settingsRes.data?.raisons_refus) && settingsRes.data.raisons_refus.length > 0) {
                setRefusalReasons(settingsRes.data.raisons_refus);
            }
        } catch (e) {
            // Non-fatal — the dossier is already displayed, we just log
            console.warn('Auxiliary data load failed', e);
        }
    };

    const handleFieldChange = (field, value) => {
        setDossier(prev => ({ ...prev, [field]: value }));
    };

    const handleStatutChange = (value) => {
        // "refuse" or any "refuse"-like statut requires a motif
        if (value === 'refuse' || value === 'devis_refuse') {
            setRefuseOpen(true);
            // stash value so dialog can confirm
            setDossier(prev => ({ ...prev, _pending_statut: value }));
            return;
        }
        handleFieldChange('statut', value);
    };

    const handleRefuseConfirm = async ({ raison_refus, raison_refus_notes }) => {
        const newStatut = dossier._pending_statut || 'refuse';
        try {
            await updateDossier(id, { statut: newStatut, raison_refus, raison_refus_notes });
            toast.success('Dossier marqué en refus');
            setRefuseOpen(false);
            fetchData();
        } catch {
            toast.error('Erreur lors de la mise à jour');
        }
    };

    const handleAutoSaveDate = async (field, value) => {
        try {
            await updateDossier(id, { [field]: value });
            toast.success('Date enregistrée');
        } catch {
            toast.error("Erreur d'enregistrement");
        }
    };

    const handleSave = async () => {
        setSaving(true);
        try {
            const updateData = {
                titre: dossier.titre,
                description: dossier.description,
                statut: dossier.statut,
                type_travaux: dossier.type_travaux,
                montant_estime: dossier.montant_estime,
                montant_devis: dossier.montant_devis,
                numero_devis: dossier.numero_devis,
                commercial_id: dossier.commercial_id,
                conducteur_id: dossier.conducteur_id,
                date_rdv: dossier.date_rdv,
                heure_rdv: dossier.heure_rdv,
                date_rdv_realise: dossier.date_rdv_realise,
                date_devis_creation: dossier.date_devis_creation,
                date_devis_envoi: dossier.date_devis_envoi,
                date_relance: dossier.date_relance,
                date_signature: dossier.date_signature,
                date_debut_chantier: dossier.date_debut_chantier,
                date_fin_chantier: dossier.date_fin_chantier,
                date_reception: dossier.date_reception,
                date_satisfaction_envoyee: dossier.date_satisfaction_envoyee,
                date_avis_google_demande: dossier.date_avis_google_demande,
                satisfaction_note: dossier.satisfaction_note,
                avis_google_obtenu: dossier.avis_google_obtenu
            };
            
            // Remove undefined/null values
            Object.keys(updateData).forEach(key => {
                if (updateData[key] === undefined || updateData[key] === '') {
                    delete updateData[key];
                }
            });

            await updateDossier(id, updateData);
            toast.success('Dossier mis à jour');
            setEditMode(false);
            fetchData();
        } catch (error) {
            const detail = error?.response?.data?.detail;
            toast.error(typeof detail === 'string' ? detail : 'Erreur lors de la sauvegarde');
        } finally {
            setSaving(false);
        }
    };

    const handleDeleteDocument = async (docId) => {
        if (!window.confirm('Supprimer ce document ?')) return;
        try {
            await deleteDocument(docId);
            toast.success('Document supprimé');
            fetchData();
        } catch (error) {
            toast.error('Erreur lors de la suppression');
        }
    };

    const handleMarkRelanceDone = async (relanceId) => {
        try {
            await updateRelance(relanceId, { 
                statut: 'envoyee', 
                date_envoi: new Date().toISOString() 
            });
            toast.success('Relance marquée comme effectuée');
            fetchData();
        } catch (error) {
            toast.error('Erreur');
        }
    };

    const handleDeleteRelance = async (relanceId) => {
        try {
            await deleteRelance(relanceId);
            toast.success('Relance supprimée');
            fetchData();
        } catch (error) {
            toast.error('Erreur');
        }
    };

    const handleDeleteDossier = async () => {
        setDeleting(true);
        try {
            await deleteDossier(id);
            toast.success('Dossier déplacé dans la corbeille');
            navigate('/projets');
        } catch (error) {
            const detail = error?.response?.data?.detail;
            toast.error(typeof detail === 'string' ? detail : 'Erreur lors de la suppression');
        } finally {
            setDeleting(false);
            setShowDeleteConfirm(false);
        }
    };

    if (loading || !dossier) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="w-8 h-8 border-4 border-[#E85A24] border-t-transparent rounded-full animate-spin" />
            </div>
        );
    }

    const formatDate = (dateStr) => {
        if (!dateStr) return '-';
        return format(new Date(dateStr), 'dd/MM/yyyy', { locale: fr });
    };

    const formatDateTime = (dateStr) => {
        if (!dateStr) return '-';
        return format(new Date(dateStr), 'dd/MM/yyyy HH:mm', { locale: fr });
    };

    const getDocumentIcon = (type) => {
        if (type?.includes('photo')) return <Image className="w-5 h-5 text-[#0284C7]" />;
        if (type === 'devis' || type === 'plan') return <FileText className="w-5 h-5 text-[#E85A24]" />;
        return <File className="w-5 h-5 text-[#52525B]" />;
    };

    return (
        <div className="space-y-4 sm:space-y-6" data-testid="dossier-page">
            {/* Header */}
            <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-3">
                <div className="flex items-start gap-3 sm:gap-4 min-w-0">
                    <Button 
                        variant="ghost" 
                        size="icon" 
                        onClick={() => navigate('/projets')}
                        className="rounded-sm shrink-0"
                    >
                        <ArrowLeft className="w-5 h-5" />
                    </Button>
                    <div className="min-w-0 flex-1">
                        <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3">
                            {editMode ? (
                                <Input
                                    value={dossier.titre}
                                    onChange={(e) => handleFieldChange('titre', e.target.value)}
                                    className="text-lg sm:text-2xl font-bold h-auto py-1 px-2"
                                    data-testid="dossier-titre-input"
                                />
                            ) : (
                                <h1 className="text-xl sm:text-2xl font-bold text-[#0A0A0A] tracking-tight break-words">
                                    {dossier.titre}
                                </h1>
                            )}
                            <Select 
                                value={dossier.statut} 
                                onValueChange={handleStatutChange}
                                disabled={!editMode}
                            >
                                <SelectTrigger 
                                    className={`w-auto px-3 py-1 h-8 text-xs font-semibold rounded-sm ${
                                        dossier.statut?.includes('accepte') || dossier.statut?.includes('termine') 
                                            ? 'bg-[#16A34A]/10 text-[#16A34A] border-[#16A34A]' 
                                            : 'bg-[#E85A24]/10 text-[#E85A24] border-[#E85A24]'
                                    }`}
                                    data-testid="dossier-statut"
                                >
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    {statuts.map(s => (
                                        <SelectItem key={s} value={s}>{STATUT_LABELS[s] || s}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        {dossier.prospect && (
                            <p className="text-[#52525B] mt-1">
                                {dossier.prospect.prenom} {dossier.prospect.nom}
                                {dossier.prospect.societe && ` - ${dossier.prospect.societe}`}
                            </p>
                        )}
                    </div>
                </div>
                <div className="flex flex-wrap gap-2">
                    <RdvGoogleButton dossierId={dossier.id} onCreated={fetchData} />
                    <Button
                        variant="outline"
                        onClick={() => navigate(`/litiges?new=1&dossier_id=${dossier.id}`)}
                        className="rounded-sm border-[#DC2626] text-[#DC2626] hover:bg-[#DC2626] hover:text-white"
                        data-testid="dossier-new-litige-btn"
                        title="Ouvrir un litige sur ce dossier"
                    >
                        <Scale className="w-4 h-4 mr-2" /> Nouveau litige
                    </Button>
                    {editMode ? (
                        <>
                            <Button 
                                variant="outline" 
                                onClick={() => { setEditMode(false); fetchData(); }}
                                className="rounded-sm"
                            >
                                Annuler
                            </Button>
                            <Button 
                                onClick={handleSave}
                                disabled={saving}
                                className="bg-[#E85A24] hover:bg-[#CC4D1C] text-white rounded-sm"
                                data-testid="save-dossier-btn"
                            >
                                <Save className="w-4 h-4 mr-2" /> Enregistrer
                            </Button>
                        </>
                    ) : (
                        <>
                            <Button
                                onClick={() => window.open(`${API_URL}/api/exports/dossier/${dossier.id}/pdf`, '_blank')}
                                variant="outline"
                                className="rounded-sm"
                                data-testid="export-dossier-pdf-btn"
                                title="Exporter la fiche projet en PDF"
                            >
                                <Download className="w-4 h-4 mr-2" /> Export PDF
                            </Button>
                            <Button 
                                onClick={() => setEditMode(true)}
                                variant="outline"
                                className="rounded-sm"
                                data-testid="edit-dossier-btn"
                            >
                                <Pencil className="w-4 h-4 mr-2" /> Modifier
                            </Button>
                            {isAdmin && (
                                <Button
                                    onClick={() => setShowDeleteConfirm(true)}
                                    variant="outline"
                                    className="rounded-sm text-[#DC2626] hover:bg-[#DC2626]/5 border-[#DC2626]/30"
                                    data-testid="delete-dossier-btn"
                                    title="Déplacer ce dossier vers la corbeille"
                                >
                                    <Trash2 className="w-4 h-4 mr-2" /> Supprimer
                                </Button>
                            )}
                        </>
                    )}
                </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Main Content */}
                <div className="lg:col-span-2 space-y-6">
                    <CoverPhotoBlock dossierId={dossier.id} hasCoverPhoto={dossier.has_cover_photo} onUpdated={fetchData} />

                    {dossier.statut === 'devis_envoye' && (
                        <PostDevisChecklist
                            dossier={dossier}
                            prospect={dossier.prospect}
                            onUpdated={fetchData}
                        />
                    )}

                    {dossier.budget_vs_realise?.alerte && (
                        <div
                            className="flex items-start gap-3 p-3 sm:p-4 bg-red-50 border border-red-200 rounded-sm"
                            data-testid="budget-overrun-banner"
                        >
                            <AlertTriangle className="w-5 h-5 text-red-600 mt-0.5 shrink-0" />
                            <div className="flex-1 text-sm">
                                <p className="font-semibold text-red-700">
                                    Écart Budget vs Réalisé au-delà du seuil (+{dossier.budget_vs_realise.ecart_pct}%)
                                </p>
                                <p className="text-red-700/80">
                                    Prévu : <b>{dossier.budget_vs_realise.total_budget.toLocaleString('fr-FR')} €</b>
                                    · Réalisé : <b>{dossier.budget_vs_realise.total_real.toLocaleString('fr-FR')} €</b>
                                    · Dépassement : <b>+{dossier.budget_vs_realise.ecart.toLocaleString('fr-FR')} €</b>.
                                    Vérifiez les onglets <a href="#" onClick={(e) => { e.preventDefault(); document.querySelector('[data-testid="tab-budget"]')?.click(); }} className="underline font-medium">Budget</a> / <a href="#" onClick={(e) => { e.preventDefault(); document.querySelector('[data-testid="tab-charges"]')?.click(); }} className="underline font-medium">Charges</a>.
                                </p>
                            </div>
                        </div>
                    )}

                    <Tabs defaultValue="infos" className="w-full">
                        <div className="overflow-x-auto -mx-3 sm:mx-0 px-3 sm:px-0">
                            <TabsList className="bg-[#F4F4F5] p-1 rounded-sm inline-flex flex-nowrap sm:flex-wrap h-auto w-max sm:w-auto">
                                <TabsTrigger value="infos" className="data-[state=active]:bg-white rounded-sm whitespace-nowrap">Informations</TabsTrigger>
                                <TabsTrigger value="budget" className="data-[state=active]:bg-white rounded-sm whitespace-nowrap" data-testid="tab-budget">
                                    Budget
                                    {dossier.budget_vs_realise?.alerte && (
                                        <span
                                            className="ml-1.5 inline-flex items-center justify-center w-2 h-2 rounded-full bg-red-500 animate-pulse"
                                            title={`Écart Réalisé vs Prévu : +${dossier.budget_vs_realise.ecart_pct}%`}
                                            data-testid="budget-alert-dot"
                                        />
                                    )}
                                </TabsTrigger>
                                <TabsTrigger value="charges" className="data-[state=active]:bg-white rounded-sm whitespace-nowrap" data-testid="tab-charges">
                                    Charges
                                    {dossier.budget_vs_realise?.alerte && (
                                        <span
                                            className="ml-1.5 inline-flex items-center justify-center w-2 h-2 rounded-full bg-red-500 animate-pulse"
                                            title={`Écart Réalisé vs Prévu : +${dossier.budget_vs_realise.ecart_pct}%`}
                                        />
                                    )}
                                </TabsTrigger>
                                <TabsTrigger value="facturation" className="data-[state=active]:bg-white rounded-sm whitespace-nowrap" data-testid="tab-facturation">Facturation</TabsTrigger>
                                <TabsTrigger value="chantier" className="data-[state=active]:bg-white rounded-sm whitespace-nowrap" data-testid="tab-chantier">Chantier</TabsTrigger>
                                <TabsTrigger value="reception" className="data-[state=active]:bg-white rounded-sm whitespace-nowrap" data-testid="tab-reception">Réception</TabsTrigger>
                                <TabsTrigger value="notes" className="data-[state=active]:bg-white rounded-sm whitespace-nowrap" data-testid="tab-notes">Notes</TabsTrigger>
                                <TabsTrigger value="dates" className="data-[state=active]:bg-white rounded-sm whitespace-nowrap">Dates clés</TabsTrigger>
                                <TabsTrigger value="documents" className="data-[state=active]:bg-white rounded-sm whitespace-nowrap">Documents ({dossier.documents?.length || 0})</TabsTrigger>
                                <TabsTrigger value="historique" className="data-[state=active]:bg-white rounded-sm whitespace-nowrap">Historique</TabsTrigger>
                            </TabsList>
                        </div>

                        <TabsContent value="infos" className="mt-4">
                            <DossierInfoTab
                                dossier={dossier}
                                editMode={editMode}
                                users={users}
                                onFieldChange={handleFieldChange}
                                onDevisUploaded={fetchData}
                            />
                        </TabsContent>

                        <TabsContent value="budget" className="mt-4">
                            <BudgetTab dossier={dossier} onChange={fetchData} />
                        </TabsContent>

                        <TabsContent value="charges" className="mt-4">
                            <ChargesTab dossier={dossier} onChange={fetchData} />
                        </TabsContent>

                        <TabsContent value="facturation" className="mt-4">
                            <FacturationTab dossier={dossier} onChange={fetchData} />
                        </TabsContent>

                        <TabsContent value="chantier" className="mt-4">
                            <ChantierTab dossier={dossier} />
                        </TabsContent>

                        <TabsContent value="reception" className="mt-4">
                            <PVReceptionTab dossier={dossier} onUpdated={fetchData} />
                        </TabsContent>

                        <TabsContent value="notes" className="mt-4">
                            <NotesTab dossier={dossier} onAddNote={() => setShowNoteModal(true)} />
                        </TabsContent>

                        <TabsContent value="dates" className="mt-4">
                            <div className="bg-white border border-[#E4E4E7] p-6">
                                <p className="text-sm text-[#52525B] mb-4">
                                    Les modifications de date sont enregistrées automatiquement.
                                </p>
                                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                                    <DateField label="Date de contact" value={dossier.date_contact} onChange={handleFieldChange} field="date_contact" onAutoSave={handleAutoSaveDate} />
                                    <DateField label="Date RDV" value={dossier.date_rdv} onChange={handleFieldChange} field="date_rdv" onAutoSave={handleAutoSaveDate} />
                                    <DateField label="RDV réalisé" value={dossier.date_rdv_realise} onChange={handleFieldChange} field="date_rdv_realise" onAutoSave={handleAutoSaveDate} />
                                    <DateField label="Création devis" value={dossier.date_devis_creation} onChange={handleFieldChange} field="date_devis_creation" onAutoSave={handleAutoSaveDate} />
                                    <DateField label="Envoi devis" value={dossier.date_devis_envoi} onChange={handleFieldChange} field="date_devis_envoi" onAutoSave={handleAutoSaveDate} />
                                    <DateField label="Signature" value={dossier.date_signature} onChange={handleFieldChange} field="date_signature" onAutoSave={handleAutoSaveDate} />
                                    <DateField label="Début chantier" value={dossier.date_debut_chantier} onChange={handleFieldChange} field="date_debut_chantier" onAutoSave={handleAutoSaveDate} />
                                    <DateField label="Fin chantier" value={dossier.date_fin_chantier} onChange={handleFieldChange} field="date_fin_chantier" onAutoSave={handleAutoSaveDate} />
                                    <DateField label="Réception" value={dossier.date_reception} onChange={handleFieldChange} field="date_reception" onAutoSave={handleAutoSaveDate} />
                                    <DateField label="Signature du PV de réception" value={dossier.date_satisfaction_envoyee} onChange={handleFieldChange} field="date_satisfaction_envoyee" onAutoSave={handleAutoSaveDate} />
                                    <DateField label="Avis Google demandé" value={dossier.date_avis_google_demande} onChange={handleFieldChange} field="date_avis_google_demande" onAutoSave={handleAutoSaveDate} />
                                </div>
                            </div>
                        </TabsContent>

                        <TabsContent value="documents" className="mt-4">
                            <div className="bg-white border border-[#E4E4E7] p-6">
                                <div className="flex items-center justify-between mb-4">
                                    <h3 className="font-semibold text-[#0A0A0A]">Documents</h3>
                                    <Button 
                                        onClick={() => setShowUploadModal(true)}
                                        className="bg-[#E85A24] hover:bg-[#CC4D1C] text-white rounded-sm"
                                        data-testid="upload-document-btn"
                                    >
                                        <Upload className="w-4 h-4 mr-2" /> Ajouter
                                    </Button>
                                </div>
                                {dossier.documents?.length === 0 ? (
                                    <div className="text-center py-8 text-[#52525B]">
                                        <FileText className="w-12 h-12 mx-auto mb-2 opacity-50" />
                                        <p>Aucun document</p>
                                    </div>
                                ) : (
                                    <div className="space-y-2">
                                        {dossier.documents?.map(doc => (
                                            <div 
                                                key={doc.id} 
                                                className="flex items-center justify-between p-3 bg-[#FAFAFA] hover:bg-[#F4F4F5] transition-colors"
                                                data-testid={`document-${doc.id}`}
                                            >
                                                <div className="flex items-center gap-3">
                                                    {getDocumentIcon(doc.type_document)}
                                                    <div>
                                                        <p className="font-medium text-[#0A0A0A]">{doc.original_filename}</p>
                                                        <p className="text-xs text-[#52525B]">
                                                            {doc.type_document} • {formatDateTime(doc.created_at)}
                                                        </p>
                                                    </div>
                                                </div>
                                                <div className="flex items-center gap-2">
                                                    <Button
                                                        variant="ghost"
                                                        size="icon"
                                                        onClick={() => setPreviewDoc(doc)}
                                                        title="Visionner"
                                                        data-testid={`preview-document-${doc.id}`}
                                                    >
                                                        <Eye className="w-4 h-4" />
                                                    </Button>
                                                    <Button
                                                        variant="ghost"
                                                        size="icon"
                                                        onClick={() => window.open(`${API_URL}/api/documents/${doc.id}/download`, '_blank')}
                                                        title="Télécharger"
                                                    >
                                                        <Download className="w-4 h-4" />
                                                    </Button>
                                                    <Button
                                                        variant="ghost"
                                                        size="icon"
                                                        onClick={() => handleDeleteDocument(doc.id)}
                                                        className="text-[#DC2626]"
                                                        title="Supprimer"
                                                    >
                                                        <Trash2 className="w-4 h-4" />
                                                    </Button>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </TabsContent>

                        <TabsContent value="historique" className="mt-4">
                            <div className="bg-white border border-[#E4E4E7] p-6">
                                <div className="flex items-center justify-between mb-4">
                                    <h3 className="font-semibold text-[#0A0A0A]">Historique des échanges</h3>
                                    <Button 
                                        onClick={() => setShowNoteModal(true)}
                                        variant="outline"
                                        className="rounded-sm"
                                        data-testid="add-note-btn"
                                    >
                                        <Plus className="w-4 h-4 mr-2" /> Ajouter une note
                                    </Button>
                                </div>
                                {dossier.interactions?.length === 0 ? (
                                    <div className="text-center py-8 text-[#52525B]">
                                        <History className="w-12 h-12 mx-auto mb-2 opacity-50" />
                                        <p>Aucun historique</p>
                                    </div>
                                ) : (
                                    <div className="space-y-3">
                                        {dossier.interactions?.map(interaction => (
                                            <div key={interaction.id} className="flex gap-3 p-3 bg-[#FAFAFA] border-l-4" style={{ borderLeftColor: colorFromId(interaction.created_by) }} data-testid={`interaction-${interaction.id}`}>
                                                <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                                                    interaction.type_interaction === 'statut' ? 'bg-[#E85A24]/10 text-[#E85A24]' :
                                                    interaction.type_interaction === 'document' ? 'bg-[#0284C7]/10 text-[#0284C7]' :
                                                    'bg-[#52525B]/10 text-[#52525B]'
                                                }`}>
                                                    {interaction.type_interaction === 'statut' ? <Clock className="w-4 h-4" /> :
                                                     interaction.type_interaction === 'document' ? <FileText className="w-4 h-4" /> :
                                                     <MessageSquare className="w-4 h-4" />}
                                                </div>
                                                <div className="flex-1">
                                                    <div className="flex items-center gap-2 mb-1">
                                                        <UserBadge userId={interaction.created_by} name={interaction.created_by_name || 'Système'} size="sm" />
                                                        <span className="text-xs text-[#52525B]">·</span>
                                                        <span className="text-xs text-[#52525B]">{formatDateTime(interaction.created_at)}</span>
                                                    </div>
                                                    <p className="text-sm">{interaction.contenu}</p>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </TabsContent>
                    </Tabs>
                </div>

                {/* Sidebar */}
                <div className="space-y-6">
                    {/* Quick Actions */}
                    <div className="bg-white border border-[#E4E4E7] p-4">
                        <h3 className="font-semibold text-[#0A0A0A] mb-3">Actions rapides</h3>
                        <div className="space-y-2">
                            <Button 
                                variant="outline" 
                                className="w-full justify-start rounded-sm"
                                onClick={() => setShowNoteModal(true)}
                            >
                                <MessageSquare className="w-4 h-4 mr-2" /> Ajouter une note
                            </Button>
                            <Button 
                                variant="outline" 
                                className="w-full justify-start rounded-sm"
                                onClick={() => setShowRelanceModal(true)}
                            >
                                <Bell className="w-4 h-4 mr-2" /> Planifier une relance
                            </Button>
                            <Button 
                                variant="outline" 
                                className="w-full justify-start rounded-sm"
                                onClick={() => setShowUploadModal(true)}
                            >
                                <Upload className="w-4 h-4 mr-2" /> Ajouter un document
                            </Button>
                        </div>
                    </div>

                    {/* Relances */}
                    <div className="bg-white border border-[#E4E4E7] p-4">
                        <div className="flex items-center justify-between mb-3">
                            <h3 className="font-semibold text-[#0A0A0A]">Relances</h3>
                            <Button 
                                variant="ghost" 
                                size="sm"
                                onClick={() => setShowRelanceModal(true)}
                            >
                                <Plus className="w-4 h-4" />
                            </Button>
                        </div>
                        {dossier.relances?.filter(r => r.statut === 'planifiee').length === 0 ? (
                            <p className="text-sm text-[#52525B]">Aucune relance planifiée</p>
                        ) : (
                            <div className="space-y-2">
                                {dossier.relances?.filter(r => r.statut === 'planifiee').map(relance => (
                                    <div key={relance.id} className="flex items-center justify-between p-2 bg-[#FAFAFA]">
                                        <div>
                                            <p className="text-sm font-medium">{relance.type_relance}</p>
                                            <p className="text-xs text-[#52525B]">{formatDate(relance.date_prevue)}</p>
                                        </div>
                                        <div className="flex gap-1">
                                            <Button 
                                                variant="ghost" 
                                                size="icon"
                                                onClick={() => handleMarkRelanceDone(relance.id)}
                                                className="h-8 w-8 text-[#16A34A]"
                                            >
                                                <CheckCircle2 className="w-4 h-4" />
                                            </Button>
                                            <Button 
                                                variant="ghost" 
                                                size="icon"
                                                onClick={() => handleDeleteRelance(relance.id)}
                                                className="h-8 w-8 text-[#DC2626]"
                                            >
                                                <XCircle className="w-4 h-4" />
                                            </Button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                    {/* Satisfaction */}
                    {['reception_effectuee', 'satisfaction_envoyee', 'avis_google_demande', 'parrainage_obtenu', 'dossier_clos'].includes(dossier.statut) && (
                        <div className="bg-white border border-[#E4E4E7] p-4">
                            <h3 className="font-semibold text-[#0A0A0A] mb-3">Satisfaction</h3>
                            <div className="space-y-3">
                                <div>
                                    <Label className="field-label">Note de satisfaction</Label>
                                    {editMode ? (
                                        <Select 
                                            value={dossier.satisfaction_note?.toString() || ''} 
                                            onValueChange={(v) => handleFieldChange('satisfaction_note', parseInt(v))}
                                        >
                                            <SelectTrigger className="h-10 rounded-sm mt-1">
                                                <SelectValue placeholder="Sélectionner" />
                                            </SelectTrigger>
                                            <SelectContent>
                                                {[1,2,3,4,5].map(n => (
                                                    <SelectItem key={n} value={n.toString()}>{n}/5</SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    ) : (
                                        <p className="mt-1">{dossier.satisfaction_note ? `${dossier.satisfaction_note}/5` : '-'}</p>
                                    )}
                                </div>
                                <div className="flex items-center justify-between">
                                    <span className="text-sm">Avis Google obtenu</span>
                                    <span className={`px-2 py-1 text-xs font-semibold rounded-sm ${
                                        dossier.avis_google_obtenu 
                                            ? 'bg-[#16A34A]/10 text-[#16A34A]' 
                                            : 'bg-[#52525B]/10 text-[#52525B]'
                                    }`}>
                                        {dossier.avis_google_obtenu ? 'Oui' : 'Non'}
                                    </span>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            <NoteDialog
                open={showNoteModal}
                onOpenChange={setShowNoteModal}
                dossierId={id}
                onAdded={fetchData}
            />

            <RelanceDialog
                open={showRelanceModal}
                onOpenChange={setShowRelanceModal}
                dossierId={id}
                onAdded={fetchData}
            />

            <UploadDocumentDialog
                open={showUploadModal}
                onOpenChange={setShowUploadModal}
                dossierId={id}
                onUploaded={fetchData}
            />

            <RefuseReasonDialog
                open={refuseOpen}
                onOpenChange={setRefuseOpen}
                reasons={refusalReasons}
                onConfirm={handleRefuseConfirm}
            />

            <DocumentPreview
                document={previewDoc}
                open={!!previewDoc}
                onOpenChange={(o) => !o && setPreviewDoc(null)}
            />

            <AlertDialog open={showDeleteConfirm} onOpenChange={(open) => { if (!open && !deleting) setShowDeleteConfirm(false); }}>
                <AlertDialogContent data-testid="delete-dossier-dialog">
                    <AlertDialogHeader>
                        <AlertDialogTitle className="flex items-center gap-2">
                            <Trash2 className="w-5 h-5 text-[#DC2626]" />
                            Supprimer ce dossier ?
                        </AlertDialogTitle>
                        <AlertDialogDescription>
                            Le dossier <span className="font-semibold text-[#0A0A0A]">{dossier.titre}</span> sera déplacé
                            dans la <strong>Corbeille</strong>. Vous pourrez le restaurer pendant <strong>30 jours</strong> depuis
                            {' '}<code>/corbeille</code>. Ensuite il sera supprimé définitivement avec ses documents, coûts et factures.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel disabled={deleting}>Annuler</AlertDialogCancel>
                        <AlertDialogAction
                            onClick={handleDeleteDossier}
                            disabled={deleting}
                            className="bg-[#DC2626] hover:bg-[#B91C1C] text-white"
                            data-testid="confirm-delete-dossier"
                        >
                            {deleting ? 'Suppression…' : 'Déplacer vers la corbeille'}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
};
